/* eslint-disable no-undef */
import {
  DeleteOutlined,
  DisconnectOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
} from '@ant-design/icons'
import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import {
  Alert,
  Button,
  Col,
  Divider,
  Modal,
  Radio,
  Row,
  Spin,
  Typography as Text,
  message,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useLocation } from 'react-router-dom'

import ReportCard from '@/components/ReportCard'
import config from '@/config'
import dimorderApi from '@/libs/api/dimorderApi'
import { isRegion, REGIONS } from '@/libs/region'
import { actions, useDispatch, useSelector } from '@/redux'
import colors from '@/style/colors'

import {
  buildKeetaAuthUrl,
  generateOAuthState,
  KEETA_AUTH_URL,
  KEETA_CALLBACK_PATH,
  KEETA_OAUTH_STATE_KEY,
} from './utils'

const STATE = {
  LOADING: 'loading',
  UNAUTHORIZED: 'unauthorized', // 尚未 OAuth
  AUTHORIZED: 'authorized', // 已 OAuth（可能已綁或未綁）
}

const KEETA_ERROR_KEY_MAP = {
  KEETA_SHOP_ALREADY_BOUND: 'shopAlreadyBound',
  KEETA_SHOP_ACCESS_DENIED: 'shopAccessDenied',
  KEETA_NOT_BOUND: 'notBound',
  KEETA_DISABLED: 'disabled',
  KEETA_LINK_EXTERNAL_STORE_ERROR: 'linkFailed',
}

/**
 * 統一處理 Keeta API 的錯誤 modal
 * @param {*} error
 * @param {Function} t
 * @param {string} operationKey
 */
function showKeetaErrorModal(error, t, operationKey) {
  const errorCode = error?.response?.data?.code
  const specificKey = KEETA_ERROR_KEY_MAP[errorCode]
  if (specificKey) {
    Modal.error({
      title: t('app.common.error'),
      content: t(`page.deliveryPlatform.keeta.error.${specificKey}`),
    })
    return
  }
  Modal.error({
    title: t('page.deliveryPlatform.keeta.error.apiDownTitle'),
    content: (
      <>
        <div>{t(`page.deliveryPlatform.keeta.error.operation.${operationKey}`)}</div>
        <div style={{ marginTop: 8 }}>{t('page.deliveryPlatform.keeta.error.apiDownContent')}</div>
      </>
    ),
  })
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 500,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  unlinkBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: colors.button,
    cursor: 'pointer',
    fontSize: 14,
    userSelect: 'none',
    '&:hover': {
      opacity: 0.8,
    },
  },
  unlinkDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  trashIcon: {
    fontSize: 18,
    color: colors.error,
    cursor: 'pointer',
  },
  emptyWrap: {
    padding: '40px 0',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 48,
    color: colors.textTertiary,
  },
  emptyText: {
    fontSize: 14,
    marginTop: 8,
    fontWeight: 500,
  },
  loginBtn: {
    backgroundColor: colors.button,
    borderColor: colors.button,
    borderRadius: 4,
    color: colors.white,
    fontSize: 15,
    height: 40,
    width: '100%',
  },
  confirmBtn: {
    backgroundColor: colors.button,
    borderColor: colors.button,
    borderRadius: 4,
    color: colors.white,
    fontSize: 15,
    height: 40,
    width: '100%',
    marginTop: 24,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '16px 0',
  },
})

export default function DeliveryPlatform(props) {
  const { t } = useTranslation()
  const classes = useStyles(props)
  const dispatch = useDispatch()
  const location = useLocation()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const isHKMerchant = isRegion(activeMerchant?.region, REGIONS.HK)

  const [pageState, setPageState] = useState(STATE.LOADING)
  const [availableStores, setAvailableStores] = useState([])
  const [selectedShopId, setSelectedShopId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const params = new URLSearchParams(location.search)
  const code = params.get('code')

  const refresh = useCallback(async () => {
    if (!activeMerchant?.id) return
    setPageState(STATE.LOADING)
    try {
      if (code) {
        await dimorderApi.instance(activeMerchant.id)?.authorizeKeeta({ authCode: code })
        // remove code from URL to prevent repeated authorization on refresh
        params.delete('code')
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
      }
      const stores = (await dimorderApi.instance(activeMerchant.id)?.getKeetaShops()) ?? []
      if (stores.length === 0) {
        setAvailableStores([])
        setSelectedShopId(null)
        setPageState(STATE.UNAUTHORIZED)
        return
      }
      setAvailableStores(stores)
      const boundId = activeMerchant.keetaShopId || null
      setSelectedShopId(boundId)
      setPageState(STATE.AUTHORIZED)
      // Edge 1: 已綁定的 store 在 Keeta 側被異動/刪除 → 提示使用者
      if (boundId && !stores.some((s) => s.keetaShopId === boundId)) {
        Modal.warning({
          title: t('page.deliveryPlatform.keeta.error.storeNotFoundTitle'),
          content: t('page.deliveryPlatform.keeta.error.storeNotFoundContent'),
        })
      }
    } catch (error) {
      setAvailableStores([])
      setSelectedShopId(null)
      setPageState(STATE.UNAUTHORIZED)
    }
  }, [activeMerchant?.id, code])

  useEffect(() => {
    refresh()
  }, [refresh])

  if (activeMerchant && !isHKMerchant) {
    return <Redirect to='/' />
  }

  const isBound = !!activeMerchant?.keetaShopId

  const handleConnectKeeta = () => {
    if (!config.keetaAppId) {
      Modal.error({
        title: t('app.common.error'),
        content: t('page.deliveryPlatform.keeta.appIdMissing'),
      })
      return
    }
    const state = generateOAuthState()
    localStorage.setItem(KEETA_OAUTH_STATE_KEY, state)
    console.log(`[state] Generated OAuth state: ${state}`) // for debugging;

    const redirectUri = `${window.location.origin}${KEETA_CALLBACK_PATH}`
    const url = buildKeetaAuthUrl({
      authUrl: KEETA_AUTH_URL,
      appId: config.keetaAppId,
      redirectUri,
      state,
    })
    window.open(url, '_blank') // 開新視窗進行 OAuth，keeta在已經有授權的情況下不會跳回redirectUri
  }

  const handleConfirm = async () => {
    if (!selectedShopId) return
    if (selectedShopId === activeMerchant?.keetaShopId) return // 未變更

    const store = availableStores.find((s) => s.keetaShopId === selectedShopId)
    if (!store) return

    setSubmitting(true)
    try {
      await dimorderApi.instance(activeMerchant.id)?.bindKeeta({
        keetaShopId: store.keetaShopId,
        shopName: store.shopName,
      })
      dispatch(
        actions.merchant.updateMerchantById(activeMerchant.id, {
          keetaShopId: store.keetaShopId,
          keetaShopName: store.shopName,
        }),
      )
      message.success(t('page.deliveryPlatform.keeta.store.bindSuccess'))
    } catch (error) {
      showKeetaErrorModal(error, t, 'bind')
    } finally {
      setSubmitting(false)
    }
  }

  // Unlink：只解除當前綁定的 store，保留 Keeta 授權 → 使用者可重新挑選
  const handleUnlink = async () => {
    setSubmitting(true)
    try {
      await dimorderApi.instance(activeMerchant.id)?.unbindKeeta()
      dispatch(
        actions.merchant.updateMerchantById(activeMerchant.id, {
          keetaShopId: '',
          keetaShopName: '',
        }),
      )
      setSelectedShopId(null)
      message.success(t('page.deliveryPlatform.keeta.store.unlinkSuccess'))
    } catch (error) {
      showKeetaErrorModal(error, t, 'unbind')
    } finally {
      setSubmitting(false)
    }
  }

  // Delete：解除綁定 + 清除 Keeta 授權狀態 → 回到「No Keeta Account」畫面
  const handleDelete = () => {
    Modal.confirm({
      title: t('page.deliveryPlatform.keeta.store.deleteConfirmTitle'),
      content: t('page.deliveryPlatform.keeta.store.deleteConfirmContent'),
      icon: <ExclamationCircleFilled />,
      okText: t('app.common.yes'),
      okType: 'danger',
      cancelText: t('app.common.no'),
      onOk: async () => {
        setSubmitting(true)
        try {
          await dimorderApi.instance(activeMerchant.id)?.unbindKeeta({ delete: true })
          dispatch(
            actions.merchant.updateMerchantById(activeMerchant.id, {
              keetaShopId: '',
              keetaShopName: '',
            }),
          )
          setSelectedShopId(null)
          setAvailableStores([])
          setPageState(STATE.UNAUTHORIZED)
          message.success(t('page.deliveryPlatform.keeta.store.deleteSuccess'))
        } catch (error) {
          showKeetaErrorModal(error, t, 'unbind')
        } finally {
          setSubmitting(false)
        }
      },
    })
  }

  const renderBody = () => {
    if (pageState === STATE.LOADING) {
      return (
        <div className={classes.emptyWrap}>
          <Spin />
        </div>
      )
    }

    if (pageState === STATE.UNAUTHORIZED) {
      return (
        <>
          <div className={classes.emptyWrap}>
            <InboxOutlined className={classes.emptyIcon} />
            <div className={classes.emptyText}>{t('page.deliveryPlatform.keeta.noAccount')}</div>
          </div>
          <Button type='primary' className={classes.loginBtn} onClick={handleConnectKeeta}>
            {t('page.deliveryPlatform.keeta.connect')}
          </Button>
        </>
      )
    }

    // AUTHORIZED
    if (availableStores.length === 0) {
      return (
        <div className={classes.emptyWrap}>
          <InboxOutlined className={classes.emptyIcon} />
          <div className={classes.emptyText}>{t('page.deliveryPlatform.keeta.store.empty')}</div>
        </div>
      )
    }

    return (
      <>
        <Radio.Group
          value={selectedShopId}
          onChange={(e) => setSelectedShopId(e.target.value)}
          disabled={submitting || isBound}
          className={classes.radioGroup}
        >
          {availableStores.map((store) => (
            <Radio key={store.keetaShopId} value={store.keetaShopId}>
              {store.shopName}
            </Radio>
          ))}
        </Radio.Group>
        {!isBound && (
          <Button
            type='primary'
            className={classes.confirmBtn}
            loading={submitting}
            disabled={submitting || !selectedShopId}
            onClick={handleConfirm}
          >
            {t('page.deliveryPlatform.keeta.confirm')}
          </Button>
        )}
      </>
    )
  }

  return (
    <PageContainer
      ghost
      header={{
        title: t('page.deliveryPlatform.title'),
      }}
    >
      <Row className={classes.container}>
        <Col xs={24} md={16} lg={12}>
          {pageState === STATE.AUTHORIZED && availableStores.length > 0 && (
            <Alert
              showIcon
              type='info'
              message={t('page.deliveryPlatform.keeta.storeInstruction')}
              style={{ marginBottom: 16 }}
            />
          )}
          <ReportCard>
            <div className={classes.cardHeader}>
              <Text className={classes.cardTitle}>
                {t('page.deliveryPlatform.keeta.cardTitle')}
              </Text>
              {isBound && pageState === STATE.AUTHORIZED && (
                <div className={classes.headerActions}>
                  <span
                    className={`${classes.unlinkBtn} ${submitting ? classes.unlinkDisabled : ''}`}
                    onClick={submitting ? undefined : handleUnlink}
                  >
                    <DisconnectOutlined />
                    {t('page.deliveryPlatform.keeta.store.unlink')}
                  </span>
                  <DeleteOutlined className={classes.trashIcon} onClick={handleDelete} />
                </div>
              )}
            </div>
            <Divider style={{ margin: '12px 0' }} />
            {renderBody()}
          </ReportCard>
        </Col>
      </Row>
    </PageContainer>
  )
}
